import React, { useMemo } from 'react';
import useData from '../hooks/useData';
import GlassCard from '../components/ui/Card';
import GlassButton from '../components/ui/Button';
import { EditIcon, DeleteIcon, CloseIcon, GroupIcon, AddIcon, RefreshIcon } from '../components/ui/Icons';
import { getStatusPillClass, getPriorityPillClass } from '../utils/helpers';

const ProjectDetails = ({ project, onBack, onEdit, onDelete, onTaskClick, onNewTaskClick }) => {
    const { tasks, clients } = useData();

    const { projectTasks, completedTasks, progress, client } = useMemo(() => {
        if (!project) return { projectTasks: [], completedTasks: [], progress: 0, client: null };

        const tasksForProject = tasks.filter(task => task.projectId === project.id);
        const completed = tasksForProject.filter(task => task.status === 'Concluído');
        const progressPercentage = tasksForProject.length > 0 ? (completed.length / tasksForProject.length) * 100 : 0;
        const associatedClient = clients.find(c => c.id === project.clientId);

        return {
            projectTasks: tasksForProject,
            completedTasks: completed,
            progress: progressPercentage,
            client: associatedClient,
        };
    }, [project, tasks, clients]);

    if (!project) return null;

    return (
        <div className="p-4 space-y-6">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h2 className="text-3xl font-bold text-white">{project.name}</h2>
                    {client && (
                        <div className="flex items-center gap-2 mt-2 text-indigo-300">
                            <GroupIcon size={16} />
                            <span className="text-lg">{client.name}</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                    <GlassButton onClick={() => onNewTaskClick('A Fazer', project.id)} className="!bg-indigo-500/80 hover:!bg-indigo-500">
                        <AddIcon size={16} />
                        <span>Adicionar Tarefa</span>
                    </GlassButton>
                    <GlassButton onClick={() => onEdit(project)}><EditIcon size={20} /></GlassButton>
                    <GlassButton onClick={() => onDelete(project)} className="!bg-red-500/30 hover:!bg-red-500/40"><DeleteIcon size={20} /></GlassButton>
                    <GlassButton onClick={onBack}><CloseIcon size={20} /></GlassButton>
                </div>
            </div>

            <GlassCard>
                <h3 className="font-bold text-lg mb-2">Progresso do Projeto</h3>
                <div className="flex justify-between items-center text-sm text-gray-300 mb-1">
                    <span>Conclusão</span>
                    <span>{completedTasks.length} de {projectTasks.length} tarefas</span>
                </div>
                <div className="w-full bg-black/30 rounded-full h-3">
                    <div className="bg-indigo-500 h-3 rounded-full transition-all duration-500 text-center text-xs text-white font-bold leading-none pt-0.5" style={{ width: `${progress}%` }}>
                        {progress.toFixed(0)}%
                    </div>
                </div>
            </GlassCard>

            <GlassCard>
                <h3 className="font-bold text-lg mb-4">Tarefas do Projeto</h3>
                <div className="space-y-3 max-h-[calc(100vh-450px)] overflow-y-auto pr-2">
                    {projectTasks.length > 0 ? projectTasks.map(task => (
                        <div 
                            key={task.id} 
                            onClick={() => onTaskClick(task)}
                            className="bg-white/5 p-3 rounded-lg flex justify-between items-center cursor-pointer hover:bg-white/10 transition-colors"
                        >
                            <div>
                                <p className="font-semibold text-white uppercase">{task.title}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {task.isAlteration && (
                                    <div className="text-orange-400" title="Tarefa marcada para alteração">
                                        <RefreshIcon size={16} />
                                    </div>
                                )}
                                 <span className={`px-2 py-1 rounded-full text-xs ${getPriorityPillClass(task.priority)}`}>
                                    {task.priority}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusPillClass(task.status)}`}>
                                    {task.status}
                                </span>
                            </div>
                        </div>
                    )) : (
                        <p className="text-gray-400 text-center py-4">Nenhuma tarefa associada a este projeto ainda.</p>
                    )}
                </div>
            </GlassCard>
        </div>
    );
};

export default ProjectDetails;